/**
 * Bulk AI operations (flag `bulk_ai_operations`): `/store/ai-bulk-jobs`.
 * Shapes mirror `src/types/aiBulkJob.ts`.
 */
import { getAuthHeaders, makeApiCall, readJsonResponse } from '../utils/apiConfig';

export type AiBulkOperation = 'rewrite_title' | 'rewrite_description' | 'fill_specs' | 'classify_tag' | 'normalize_name';
export type AiBulkTarget = 'overrideTitle' | 'overrideDescription' | 'overrideTags' | 'aiSpecSuggestions';
export type AiBulkJobStatus = 'estimated' | 'running' | 'previewed' | 'budget_exhausted' | 'applied' | 'failed';
export type AiBulkRowStatus = 'pending' | 'done' | 'error' | 'skipped';
export type AiBulkRowDecision = 'pending' | 'approved' | 'rejected';
export type AiBulkMissingField = 'description' | 'shortDescription' | 'features' | 'specifications' | 'tags';

export interface AiBulkFilter {
  brands?: string[];
  productTypes?: Array<'camera' | 'lens' | 'accessory'>;
  search?: string;
  missingField?: AiBulkMissingField;
  productIds?: string[];
}

export interface AiBulkJob {
  id: string;
  operation: AiBulkOperation;
  target: AiBulkTarget;
  filter: AiBulkFilter;
  parameters: { voiceRules?: string | null };
  status: AiBulkJobStatus;
  productCount: number;
  truncated: boolean;
  estimate: { calls: number; cachedRows: number; approxCostUsd: number };
  spent: { calls: number; approxCostUsd: number };
  progress: { total: number; pending: number; done: number; error: number; skipped: number };
  decisions: { pending: number; approved: number; rejected: number };
  appliedCount: number;
  stopReason: string | null;
  createdAt: string | null;
  lastRunAt: string | null;
  appliedAt: string | null;
}

export interface AiBulkRow {
  id: string;
  productId: string;
  productName: string;
  brand: string;
  sku: string | null;
  order: number;
  status: AiBulkRowStatus;
  before: unknown;
  after: unknown;
  decision: AiBulkRowDecision;
  error: string | null;
  cached: boolean;
  applied: boolean;
}

export interface AiBulkUsage {
  month: string;
  used: number;
  /** -1 means no cap. */
  limit: number;
  remaining: number | null;
  maxProducts: number;
  batchSize: number;
  operations: Array<{ key: AiBulkOperation; label: string }>;
}

const call = async <T>(path: string, init: RequestInit, fallback: string): Promise<T> => {
  const response = await makeApiCall(path, { ...init, headers: { ...(await getAuthHeaders()), ...(init.headers || {}) } });
  const body = await readJsonResponse<any>(response, fallback);
  if (!response.ok || body?.success === false) {
    throw new Error(body?.error?.message || body?.error || body?.message || fallback);
  }
  return body.data as T;
};

/** Store-scoped spec suggestions on a product's override (MOT-400): label -> value. */
export interface AiSpecSuggestionOverride {
  aiSpecSuggestions?: Record<string, string> | null;
  overrideSpecs?: Record<string, string> | null;
}

async function actOnSpecSuggestion(
  action: 'use' | 'dismiss',
  productId: string,
  label: string,
  fallback: string,
): Promise<AiSpecSuggestionOverride> {
  const response = await makeApiCall(`/store/ai-bulk-jobs/spec-suggestions/${encodeURIComponent(productId)}/${action}`, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ label }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) throw new Error(body?.error?.message || body?.message || fallback);
  return body.override as AiSpecSuggestionOverride;
}

export const applyAiSpecSuggestion = (productId: string, label: string, fallback: string) =>
  actOnSpecSuggestion('use', productId, label, fallback);
export const dismissAiSpecSuggestion = (productId: string, label: string, fallback: string) =>
  actOnSpecSuggestion('dismiss', productId, label, fallback);

export const aiBulkApi = {
  getUsage: () => call<AiBulkUsage>('/store/ai-bulk-jobs/usage', {}, 'Could not load your AI budget'),
  listJobs: () => call<AiBulkJob[]>('/store/ai-bulk-jobs?limit=50', {}, 'Could not load your jobs'),
  getJob: (id: string) => call<AiBulkJob>(`/store/ai-bulk-jobs/${encodeURIComponent(id)}`, {}, 'Could not load the job'),
  listRows: (id: string) => call<AiBulkRow[]>(`/store/ai-bulk-jobs/${encodeURIComponent(id)}/rows`, {}, 'Could not load the rows'),
  createJob: (body: { operation: AiBulkOperation; filter: AiBulkFilter; parameters: { voiceRules?: string | null } }) =>
    call<AiBulkJob>(
      '/store/ai-bulk-jobs',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      'Could not estimate the job',
    ),
  run: (id: string) =>
    call<AiBulkJob>(`/store/ai-bulk-jobs/${encodeURIComponent(id)}/run`, { method: 'POST' }, 'The run did not finish'),
  decide: (id: string, body: { rowIds?: string[]; all?: true; decision: AiBulkRowDecision }) =>
    call<AiBulkJob>(
      `/store/ai-bulk-jobs/${encodeURIComponent(id)}/rows/decision`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
      'Could not save the decision',
    ),
  apply: (id: string) =>
    call<{ job: AiBulkJob; applied: number; failed: number }>(
      `/store/ai-bulk-jobs/${encodeURIComponent(id)}/apply`,
      { method: 'POST' },
      'Could not apply the approved rows',
    ),
};
